import { defineStore } from "pinia";

interface SessionState {
  uid: string;
  sessionId: string;
  twoFactorVerified: boolean;
  lastReauthAt: number;
  recoveryMode?: boolean;
  exp: number;
}

function decodeJwtPayload(token: string): SessionState | null {
  try {
    const base64 = token.split(".")[1] ?? "";
    const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as SessionState;
  } catch {
    return null;
  }
}

const useAuthStore = defineStore("auth", () => {
  const accessToken = ref<string | null>(null);
  const tokenExpiresAt = ref<number | null>(null);
  const twoFactorVerified = ref(false);
  const twoFactorMethod = ref<"email_otp" | "totp" | null>(null);
  const recoveryMode = ref(false);
  // set to true when the session is revoked server-side; plugins watch this to
  // trigger immediate navigation to /login (stores cannot call navigateTo directly)
  const sessionRevoked = ref(false);

  let refreshTimer: ReturnType<typeof setTimeout> | null = null;

  function setSession(token: string): void {
    const payload = decodeJwtPayload(token);
    if (!payload) return;

    accessToken.value = token;
    tokenExpiresAt.value = payload.exp * 1000;
    twoFactorVerified.value = payload.twoFactorVerified;
    recoveryMode.value = payload.recoveryMode ?? false;
    // clear any stale revoked flag from a previous logout so middleware
    // doesn't keep redirecting a freshly authenticated user to /login
    sessionRevoked.value = false;

    scheduleRefresh(payload.exp);
  }

  function clearSession(): void {
    accessToken.value = null;
    tokenExpiresAt.value = null;
    twoFactorVerified.value = false;
    recoveryMode.value = false;
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  }

  function scheduleRefresh(expTimestamp: number): void {
    if (refreshTimer) clearTimeout(refreshTimer);

    const expiresInMs = expTimestamp * 1000 - Date.now();
    // fire 30s before expiry so there's a brief overlap window
    const refreshInMs = Math.max(expiresInMs - 30 * 1000, 0);

    refreshTimer = setTimeout(() => {
      refreshAccessToken();
    }, refreshInMs);
  }

  async function forceSignOut(): Promise<void> {
    clearSession();
    const { getAuth, signOut } = await import("firebase/auth");
    await signOut(getAuth()).catch(() => {});
    // set after Firebase signout so auth middleware sees no user when it runs
    sessionRevoked.value = true;
  }

  async function refreshAccessToken(): Promise<void> {
    try {
      const data = await $fetch<{ accessToken: string }>("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
      });
      if (data.accessToken) {
        setSession(data.accessToken);
      }
    } catch (err: unknown) {
      const statusCode = (err as { statusCode?: number })?.statusCode;
      if (statusCode === 401) {
        await forceSignOut();
      } else {
        clearSession();
      }
    }
  }

  async function initSession(): Promise<boolean> {
    try {
      const data = await $fetch<{ accessToken: string }>("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
      });
      if (data.accessToken) {
        setSession(data.accessToken);
        return true;
      }
    } catch {
      // no active session — try to establish one from Firebase
    }
    return false;
  }

  return {
    accessToken: readonly(accessToken),
    tokenExpiresAt: readonly(tokenExpiresAt),
    twoFactorVerified: readonly(twoFactorVerified),
    twoFactorMethod: readonly(twoFactorMethod),
    recoveryMode: readonly(recoveryMode),
    sessionRevoked: readonly(sessionRevoked),
    setSession,
    clearSession,
    forceSignOut,
    refreshAccessToken,
    initSession,
  };
});

export default useAuthStore;
